import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Users,
  CalendarCheck,
  MessageSquare,
  Lock,
  Loader2,
  TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECRET_KEY = "computheer_admin_secret";

const AdminStats = () => {
  const [secret, setSecret] = useState(() => localStorage.getItem(SECRET_KEY) || "");
  const [secretInput, setSecretInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!secret) return;
    setLoading(true);
    axios
      .get(`${API}/admin/stats`, { params: { secret } })
      .then((res) => setData(res.data))
      .catch((e) => {
        if (e.response?.status === 403) {
          localStorage.removeItem(SECRET_KEY);
          setSecret("");
        }
      })
      .finally(() => setLoading(false));
  }, [secret]);

  const handleSecretSubmit = (e) => {
    e.preventDefault();
    if (!secretInput.trim()) return;
    localStorage.setItem(SECRET_KEY, secretInput.trim());
    setSecret(secretInput.trim());
  };

  if (!secret) {
    return (
      <section className="min-h-[60vh] py-20 bg-neutral-50">
        <div className="max-w-md mx-auto px-5 md:px-10">
          <div className="bg-white border border-neutral-200 p-8">
            <div className="w-12 h-12 bg-neutral-900 text-white flex items-center justify-center mb-5">
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">Beheerderstoegang</h1>
            <p className="text-neutral-600 text-sm mb-6">
              Voer je admin-sleutel in om de statistieken te bekijken.
            </p>
            <form onSubmit={handleSecretSubmit} className="space-y-4">
              <input
                type="password"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                autoFocus
                placeholder="Admin-sleutel"
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 text-sm"
              />
              <button
                type="submit"
                className="w-full bg-neutral-900 hover:bg-black text-white px-5 py-3 text-sm font-medium"
              >
                Toegang krijgen
              </button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  const totalViews = data
    ? Object.values(data.daily_pageviews || {}).reduce((a, b) => a + b, 0)
    : 0;

  const dailyEntries = data
    ? Object.entries(data.daily_pageviews || {}).sort((a, b) => a[0].localeCompare(b[0]))
    : [];

  const maxDaily = dailyEntries.length
    ? Math.max(...dailyEntries.map(([, v]) => v))
    : 1;

  return (
    <section className="min-h-[60vh] py-20 bg-neutral-50">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">Admin</p>
            <h1 className="text-3xl font-bold text-neutral-900">Statistieken</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/crm"
              className="text-sm text-neutral-600 hover:text-neutral-900 underline"
            >
              CRM & Facturatie
            </Link>
            <Link
              to="/admin/connect"
              className="text-sm text-neutral-600 hover:text-neutral-900 underline"
            >
              Calendar koppeling
            </Link>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Statistieken laden...
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <KPI
                icon={<TrendingUp size={18} />}
                label="Pageviews (30 dagen)"
                value={totalViews}
              />
              <KPI
                icon={<CalendarCheck size={18} />}
                label="Afspraken totaal"
                value={data.appointments.total}
                sub={`${data.appointments.pending} open · ${data.appointments.confirmed} bevestigd`}
              />
              <KPI
                icon={<MessageSquare size={18} />}
                label="Contact berichten"
                value={data.contact_messages}
              />
              <KPI
                icon={<Users size={18} />}
                label="Top pagina"
                value={data.popular_paths[0]?.[0] || "-"}
                sub={data.popular_paths[0] ? `${data.popular_paths[0][1]} views` : ""}
                small
              />
            </div>

            {/* Daily chart */}
            <div className="bg-white border border-neutral-200 p-6 mb-8">
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 size={18} className="text-neutral-900" />
                <h2 className="text-lg font-bold text-neutral-900">Dagelijkse bezoekers</h2>
              </div>
              {dailyEntries.length === 0 ? (
                <p className="text-sm text-neutral-500">Nog geen data — wacht op bezoekers.</p>
              ) : (
                <div className="flex items-end gap-1 h-44">
                  {dailyEntries.map(([d, v]) => (
                    <div key={d} className="flex-1 flex flex-col items-center group">
                      <div className="text-[10px] text-neutral-400 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {v}
                      </div>
                      <div
                        className="w-full bg-neutral-900 hover:bg-neutral-700 transition-colors min-h-[2px]"
                        style={{ height: `${(v / maxDaily) * 100}%` }}
                        title={`${d}: ${v} views`}
                      />
                      <div className="text-[9px] text-neutral-400 mt-1">{d.slice(8)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Popular pages */}
            <div className="bg-white border border-neutral-200 p-6">
              <h2 className="text-lg font-bold text-neutral-900 mb-5">Populairste pagina&apos;s</h2>
              {data.popular_paths.length === 0 ? (
                <p className="text-sm text-neutral-500">Nog geen data.</p>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {data.popular_paths.map(([path, count]) => (
                    <li key={path} className="flex items-center justify-between py-3">
                      <span className="text-sm text-neutral-700">{path}</span>
                      <span className="text-sm font-semibold text-neutral-900">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-8 p-5 bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-900">
                💡 <strong>Tip:</strong> voor uitgebreide statistieken (apparaten, bron, conversies)
                koppel <strong>Google Analytics 4</strong> via je{" "}
                <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">GA4 Measurement ID</code>{" "}
                in <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">public/index.html</code>.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const KPI = ({ icon, label, value, sub, small = false }) => (
  <div className="bg-white border border-neutral-200 p-5">
    <div className="flex items-center gap-2 text-neutral-600 mb-3">
      {icon}
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </div>
    <p className={`font-bold text-neutral-900 ${small ? "text-lg break-all" : "text-3xl"}`}>
      {value}
    </p>
    {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
  </div>
);

export default AdminStats;
